/**
 * Phase 8 T-12 §12 F — Post-interview summary right rail.
 *
 * Three tabs:
 *  - Details  : meeting metadata (mirrors the live view's Details tab).
 *  - Templates: "Create Template" form (Template Name + AI Prompt textarea).
 *               Persisted server-side via `interviewTemplatesService` +
 *               TanStack Query mutations (C-25) — swapped in from the
 *               previous localStorage-keyed store.
 *  - Agent    : Q&A input piped to the same orchestrator used by
 *               `CandidateQARail`, scoped to this interview session.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Copy,
  Loader2,
  Pencil,
  Plus,
  Send,
  Sparkles,
  StopCircle,
  Trash2,
  User as UserIcon,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@core/auth/useAuth';
import { invokeLlamaChat, type LlamaChatMessage } from '@core/ai/llamaChat';
import {
  AGENT_SYSTEM_PROMPT,
  TOOL_DEFINITIONS,
  OPENAI_TOOL_DEFINITIONS,
  parseOpenAIToolCalls,
  parseToolCall,
  executeTool,
  toolResultSummary,
  augmentAssistantHistoryForLlm,
} from '@core/ai/agentTools';
import {
  interviewTemplatesService,
  type InterviewTemplate,
} from '@core/supabase/services/interviewTemplatesService';
import { queryKeys } from '@core/query/queryKeys';

export type InterviewSummaryRailTab = 'details' | 'templates' | 'agent';

export interface InterviewTemplateRailDetails {
  meetingTitle: string;
  meetingUrl: string;
  sessionType: string;
  language: string;
  companyName: string;
  candidateName: string;
  candidateEmail?: string | null;
  scheduledAt?: string | null;
  completedAt?: string | null;
  jobTitle?: string | null;
}

export interface InterviewTemplateRailProps {
  sessionId: string;
  candidateId: string;
  candidateName: string;
  details: InterviewTemplateRailDetails;
}

function DetailsTab({ details }: { details: InterviewTemplateRailDetails }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);
  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(details.meetingUrl);
      setCopied(true);
    } catch {
      /* ignore */
    }
  };
  return (
    <div className="space-y-4 px-4 py-4 text-[12px] leading-relaxed">
      <div>
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">
          Meeting
        </p>
        <p className="text-[13px] font-medium vscode-text-primary">{details.meetingTitle}</p>
      </div>
      <div>
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">
          Meeting URL
        </p>
        <div
          className="flex items-center gap-2 rounded-[10px] border px-2.5 py-1.5"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <span className="min-w-0 flex-1 truncate text-[12px] vscode-text-primary" title={details.meetingUrl}>
            {details.meetingUrl.replace(/^https?:\/\//, '')}
          </span>
          <button
            type="button"
            onClick={() => void copyUrl()}
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] vscode-text-secondary hover:bg-[var(--vscode-bg-hover)]"
            aria-label={copied ? 'Copied' : 'Copy meeting URL'}
            title={copied ? 'Copied' : 'Copy meeting URL'}
          >
            <Copy className="h-3 w-3" aria-hidden />
          </button>
        </div>
      </div>
      <dl className="grid grid-cols-[minmax(0,88px)_minmax(0,1fr)] gap-x-2 gap-y-1.5">
        <dt className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">Round</dt>
        <dd className="text-[12px] vscode-text-primary capitalize">{details.sessionType}</dd>
        <dt className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">Language</dt>
        <dd className="text-[12px] vscode-text-primary font-mono">{details.language}</dd>
        {details.jobTitle ? (
          <>
            <dt className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">Role</dt>
            <dd className="text-[12px] vscode-text-primary">{details.jobTitle}</dd>
          </>
        ) : null}
        {details.scheduledAt ? (
          <>
            <dt className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">Scheduled</dt>
            <dd className="text-[12px] vscode-text-primary tabular-nums">{details.scheduledAt}</dd>
          </>
        ) : null}
        {details.completedAt ? (
          <>
            <dt className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">Completed</dt>
            <dd className="text-[12px] vscode-text-primary tabular-nums">{details.completedAt}</dd>
          </>
        ) : null}
      </dl>
      <div>
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">
          Company
        </p>
        <p className="text-[12px] vscode-text-primary">{details.companyName}</p>
      </div>
      <div>
        <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">
          Candidate
        </p>
        <p className="text-[12px] vscode-text-primary">{details.candidateName}</p>
        {details.candidateEmail ? (
          <p className="text-[11px] vscode-text-secondary">{details.candidateEmail}</p>
        ) : null}
      </div>
    </div>
  );
}

function TemplatesTab({ orgId }: { orgId: string }) {
  const queryClient = useQueryClient();
  const enabled = Boolean(orgId);

  const {
    data: templates = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery<InterviewTemplate[]>({
    queryKey: queryKeys.interviewTemplates.list(orgId),
    queryFn: () => interviewTemplatesService.list(orgId),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saveFlash, setSaveFlash] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  useEffect(() => {
    if (!saveFlash) return;
    const t = window.setTimeout(() => setSaveFlash(null), 1800);
    return () => window.clearTimeout(t);
  }, [saveFlash]);

  const resetForm = useCallback(() => {
    setName('');
    setPrompt('');
    setEditingId(null);
  }, []);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.interviewTemplates.list(orgId) });
  }, [queryClient, orgId]);

  const saveMutation = useMutation({
    mutationFn: async (input: { id: string | null; name: string; body: string }) =>
      interviewTemplatesService.save(orgId, {
        id: input.id ?? undefined,
        name: input.name,
        body: input.body,
      }),
    onSuccess: (_row, variables) => {
      setSaveFlash(variables.id ? 'Template updated.' : 'Template created.');
      setMutationError(null);
      resetForm();
      invalidate();
    },
    onError: (err: unknown) => {
      setMutationError(err instanceof Error ? err.message : 'Could not save template.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => interviewTemplatesService.delete(orgId, id),
    onSuccess: (_data, id) => {
      setMutationError(null);
      if (editingId === id) resetForm();
      invalidate();
    },
    onError: (err: unknown) => {
      setMutationError(err instanceof Error ? err.message : 'Could not delete template.');
    },
  });

  const onSubmit = () => {
    const n = name.trim();
    const p = prompt.trim();
    if (!n || !p || !enabled || saveMutation.isPending) return;
    saveMutation.mutate({ id: editingId, name: n, body: p });
  };

  const onEdit = (t: InterviewTemplate) => {
    setEditingId(t.id);
    setName(t.name);
    setPrompt(t.body);
  };

  const onDelete = (id: string) => {
    if (!enabled || deleteMutation.isPending) return;
    deleteMutation.mutate(id);
  };

  const isSaving = saveMutation.isPending;
  const isDeleting = deleteMutation.isPending;
  const listErrorMsg = isError ? (error instanceof Error ? error.message : 'Failed to load templates.') : null;

  const footerText = (() => {
    if (mutationError) return mutationError;
    if (saveFlash) return saveFlash;
    if (editingId) return 'Editing template';
    if (!enabled) return 'Sign in to save templates';
    return 'Saved to your org · available across sessions';
  })();

  const footerTone = mutationError ? 'text-red-500' : 'vscode-text-tertiary';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 space-y-3 border-b px-4 py-4" style={{ borderColor: 'var(--vscode-border-primary)' }}>
        <div>
          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">
            Template name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Screening call summary"
            disabled={!enabled || isSaving}
            className="h-9 w-full rounded-[8px] border bg-[var(--vscode-bg-primary)] px-3 text-[13px] vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none disabled:opacity-60"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">
            AI prompt
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe how this interview should be summarised…"
            rows={5}
            disabled={!enabled || isSaving}
            className="w-full resize-y rounded-[10px] border bg-[var(--vscode-bg-primary)] px-3 py-2 text-[12px] leading-relaxed vscode-text-primary placeholder:vscode-text-tertiary focus:outline-none disabled:opacity-60"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          />
        </div>
        <div className="flex items-center justify-between gap-2">
          <p className={`text-[11px] ${footerTone}`}>{footerText}</p>
          <div className="flex items-center gap-2">
            {editingId ? (
              <button
                type="button"
                onClick={resetForm}
                disabled={isSaving}
                className="h-8 rounded-[8px] border bg-transparent px-3 text-[12px] font-medium vscode-text-primary hover:bg-[var(--vscode-bg-hover)] disabled:opacity-60"
                style={{ borderColor: 'var(--vscode-border-primary)' }}
              >
                Cancel
              </button>
            ) : null}
            <button
              type="button"
              onClick={onSubmit}
              disabled={!enabled || !name.trim() || !prompt.trim() || isSaving}
              className="inline-flex h-8 items-center gap-1.5 rounded-[8px] px-3 text-[12px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              style={{ background: 'var(--ggj-accent,#10B981)' }}
            >
              {isSaving ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
              ) : (
                <Plus className="h-3 w-3" aria-hidden />
              )}
              {editingId ? (isSaving ? 'Saving…' : 'Save template') : isSaving ? 'Creating…' : 'Create Template'}
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider vscode-text-tertiary">
            Saved templates ({templates.length})
          </p>
          {isFetching && !isLoading ? (
            <Loader2 className="h-3 w-3 animate-spin vscode-text-tertiary" aria-hidden />
          ) : null}
        </div>

        {isLoading ? (
          <ul className="space-y-2" aria-hidden>
            {Array.from({ length: 2 }).map((_, i) => (
              <li
                key={i}
                className="h-14 animate-pulse rounded-[10px] border bg-[var(--vscode-bg-hover)]/40"
                style={{ borderColor: 'var(--vscode-border-primary)' }}
              />
            ))}
          </ul>
        ) : isError ? (
          <div
            className="flex flex-col gap-2 rounded-[10px] border px-3 py-3 text-[12px]"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          >
            <p className="vscode-text-primary">
              Could not load templates{listErrorMsg ? ` · ${listErrorMsg}` : ''}
            </p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="self-start rounded-[6px] border px-2 py-1 text-[11px] font-medium vscode-text-primary hover:bg-[var(--vscode-bg-hover)]"
              style={{ borderColor: 'var(--vscode-border-primary)' }}
            >
              Retry
            </button>
          </div>
        ) : templates.length === 0 ? (
          <p className="text-[11px] italic vscode-text-tertiary">
            No custom templates yet. Create one above to reuse across interviews.
          </p>
        ) : (
          <ul className="space-y-2">
            {templates.map((t) => {
              const rowDeleting = isDeleting && deleteMutation.variables === t.id;
              return (
                <li
                  key={t.id}
                  className="group rounded-[10px] border px-3 py-2"
                  style={{ borderColor: 'var(--vscode-border-primary)' }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="min-w-0 flex-1 truncate text-[12px] font-medium vscode-text-primary">{t.name}</p>
                    <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                      <button
                        type="button"
                        onClick={() => onEdit(t)}
                        disabled={isSaving || rowDeleting}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] vscode-text-secondary hover:bg-[var(--vscode-bg-hover)] disabled:opacity-40"
                        aria-label="Edit template"
                        title="Edit template"
                      >
                        <Pencil className="h-3 w-3" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(t.id)}
                        disabled={rowDeleting}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-[6px] text-red-500 hover:bg-red-500/10 disabled:opacity-40"
                        aria-label="Delete template"
                        title="Delete template"
                      >
                        {rowDeleting ? (
                          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                        ) : (
                          <Trash2 className="h-3 w-3" aria-hidden />
                        )}
                      </button>
                    </div>
                  </div>
                  <p className="mt-1 line-clamp-3 text-[11px] vscode-text-secondary">{t.body}</p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

interface ThreadMsg {
  id: string;
  role: 'user' | 'agent';
  text: string;
  thinking?: boolean;
}

function AgentMsgRow({ msg }: { msg: ThreadMsg }) {
  const isUser = msg.role === 'user';
  return (
    <div className="flex gap-2 py-2">
      <div
        className={[
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
          isUser
            ? 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200'
            : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
        ].join(' ')}
      >
        {isUser ? <UserIcon className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide vscode-text-tertiary">
          {isUser ? 'You' : 'AI'}
        </p>
        {msg.thinking ? (
          <span className="flex items-center gap-1 text-[12px] italic vscode-text-tertiary">
            <Loader2 className="h-3 w-3 animate-spin" /> Thinking…
          </span>
        ) : (
          <p className="whitespace-pre-wrap text-[13px] leading-relaxed vscode-text-primary">{msg.text}</p>
        )}
      </div>
    </div>
  );
}

function AgentTab({
  sessionId,
  candidateId,
  candidateName,
}: {
  sessionId: string;
  candidateId: string;
  candidateName: string;
}) {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const [messages, setMessages] = useState<ThreadMsg[]>([]);
  const [history, setHistory] = useState<LlamaChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const systemPrompt = useMemo(
    () => `${AGENT_SYSTEM_PROMPT}
When the thread contains [Agent context] under a previous reply, use it for follow-up questions instead of calling tools again.
The user is asking about interview session [Session context: ${sessionId}] for candidate [Candidate context: ${candidateId}] (${candidateName}). Focus responses on this interview and candidate.

${TOOL_DEFINITIONS}`,
    [sessionId, candidateId, candidateName],
  );

  const run = useCallback(
    async (text: string) => {
      if (!text.trim() || streaming) return;
      abortRef.current = false;
      setStreaming(true);
      const userMsg: ThreadMsg = { id: crypto.randomUUID(), role: 'user', text: text.trim() };
      const thinking: ThreadMsg = { id: crypto.randomUUID(), role: 'agent', text: '', thinking: true };
      setMessages((m) => [...m, userMsg, thinking]);
      setDraft('');
      const nextHistory: LlamaChatMessage[] = [...history, { role: 'user', content: text.trim() }];
      try {
        const resp = await invokeLlamaChat({
          orgId,
          messages: nextHistory,
          systemPrompt,
          tools: OPENAI_TOOL_DEFINITIONS,
        });
        if (abortRef.current) return;
        const rawContent = resp.choices?.[0]?.message?.content ?? '';
        const toolCalls =
          parseOpenAIToolCalls(resp.choices?.[0]?.message) ??
          (rawContent ? [parseToolCall(rawContent)].filter(Boolean) : []);
        let finalText = rawContent;
        const assistantHistory = augmentAssistantHistoryForLlm(
          resp.choices?.[0]?.message ?? { role: 'assistant', content: rawContent },
        );
        if (toolCalls && toolCalls.length > 0) {
          const results = await Promise.all(toolCalls.map((tc) => executeTool(tc!, orgId)));
          const summaries = results.map((r, i) => toolResultSummary(toolCalls![i]!, r)).join('\n\n');
          finalText = summaries || rawContent;
          const toolResultHistory: LlamaChatMessage[] = results.map((r, i) => ({
            role: 'tool',
            content: JSON.stringify(r),
            tool_call_id: (toolCalls![i] as Record<string, unknown>)?.id as string | undefined,
          }));
          setHistory([...nextHistory, assistantHistory, ...toolResultHistory]);
        } else {
          setHistory([...nextHistory, { role: 'assistant', content: rawContent }]);
        }
        setMessages((m) =>
          m.map((msg) => (msg.id === thinking.id ? { ...msg, text: finalText, thinking: false } : msg)),
        );
      } catch (err) {
        if (abortRef.current) return;
        const errText = err instanceof Error ? err.message : 'Something went wrong.';
        setMessages((m) =>
          m.map((msg) => (msg.id === thinking.id ? { ...msg, text: errText, thinking: false } : msg)),
        );
      } finally {
        if (!abortRef.current) setStreaming(false);
        window.requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }));
      }
    },
    [streaming, history, orgId, systemPrompt],
  );

  const stop = () => {
    abortRef.current = true;
    setStreaming(false);
    setMessages((m) => m.filter((msg) => !msg.thinking));
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 px-4 py-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/20">
              <Sparkles className="h-6 w-6 text-emerald-500" />
            </div>
            <div>
              <p className="text-[14px] font-semibold vscode-text-primary">Ask the agent about this interview</p>
              <p className="mt-1 text-[12px] vscode-text-secondary leading-relaxed">
                Summaries, strengths, follow-up questions<br />— scoped to this session.
              </p>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-[var(--vscode-border-primary)] px-4">
            {messages.map((msg) => (
              <AgentMsgRow key={msg.id} msg={msg} />
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>
      <div className="shrink-0 border-t p-3" style={{ borderColor: 'var(--vscode-border-primary)' }}>
        <div
          className="flex items-end gap-2 rounded-[10px] border px-2 py-1.5"
          style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-primary)' }}
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void run(draft);
              }
            }}
            rows={2}
            placeholder={`Ask about ${candidateName}'s interview…`}
            className="min-h-[36px] flex-1 resize-none bg-transparent text-[13px] leading-relaxed outline-none vscode-text-primary placeholder:vscode-text-tertiary"
          />
          {streaming ? (
            <button
              type="button"
              onClick={stop}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
              title="Stop"
            >
              <StopCircle className="h-4 w-4" aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void run(draft)}
              disabled={!draft.trim()}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
              style={{ background: 'var(--ggj-accent,#10B981)' }}
              title="Send"
            >
              <Send className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function InterviewTemplateRail({
  sessionId,
  candidateId,
  candidateName,
  details,
}: InterviewTemplateRailProps) {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const [tab, setTab] = useState<InterviewSummaryRailTab>('details');

  return (
    <aside
      className="flex h-full min-h-0 w-full flex-col border-l"
      style={{ borderColor: 'var(--vscode-border-primary)' }}
      aria-label="Interview summary rail"
    >
      <nav
        className="flex shrink-0 items-center gap-1 border-b px-2 pt-2"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
        role="tablist"
      >
        {(['details', 'templates', 'agent'] as const).map((t) => {
          const active = t === tab;
          const label = t === 'details' ? 'Details' : t === 'templates' ? 'Templates' : 'Agent';
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t)}
              className={`relative h-9 px-3 text-[12px] font-medium transition-colors ${
                active ? 'vscode-text-primary' : 'vscode-text-tertiary hover:vscode-text-primary'
              }`}
            >
              {label}
              {active ? (
                <span
                  className="absolute inset-x-2 bottom-0 h-[2px] rounded-t-full"
                  style={{ background: 'var(--ggj-accent,#10B981)' }}
                />
              ) : null}
            </button>
          );
        })}
      </nav>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {tab === 'details' ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <DetailsTab details={details} />
          </div>
        ) : tab === 'templates' ? (
          <TemplatesTab orgId={orgId} />
        ) : (
          <AgentTab sessionId={sessionId} candidateId={candidateId} candidateName={candidateName} />
        )}
      </div>
    </aside>
  );
}
